/**
 * Google Analytics utility for tracking events in the React frontend
 * Based on the patterns from the old Laravel frontend
 */

import {
  GA_MEASUREMENT_ID,
  GA_GTAG_PRIVACY_OPTIONS,
  shouldUseGoogleAnalytics,
} from '../config/analyticsConfig';

// Check if gtag is available
const isGtagAvailable = () => {
  return typeof window !== 'undefined' && typeof window.gtag === 'function' && window.gtag;
};

const isAnalyticsActive = () =>
  typeof window !== 'undefined' && shouldUseGoogleAnalytics(window.location.hostname);

/**
 * identifyUser — intentionally disabled for COPPA / GDPR compliance.
 *
 * This app targets children under 13. Sending a persistent user identifier
 * (Firebase UID) to GA4 would constitute PII collection from minors, which is
 * prohibited under COPPA and requires explicit parental consent under GDPR-K.
 * All GA tracking in this app is anonymous and aggregated (no user_id, no
 * cross-session cookies — see GA_GTAG_PRIVACY_OPTIONS in analyticsConfig.js).
 *
 * The function is kept as a no-op so call sites compile without changes.
 * Do not restore the gtag('set', { user_id }) call without a full privacy review.
 */
// eslint-disable-next-line no-unused-vars
export const identifyUser = (_uid) => {};

// Main tracking function
export const trackEvent = (eventName, eventCategory, eventLabel, additionalParams = {}) => {
  const eventData = {
    event_category: eventCategory,
    event_label: eventLabel,
    ...additionalParams
  };

  if (!isAnalyticsActive()) {
    if (import.meta.env?.DEV) {
      console.debug('[GA]', eventName, eventData, '(local preview; not sent)');
    }
    return;
  }
  if (!isGtagAvailable()) {
    console.debug('[GA] gtag not available, skipping event:', eventName);
    return;
  }

  window.gtag('event', eventName, eventData);
  console.debug('[GA]', eventName, eventData);
};

// User Action Events
export const trackUserAction = {
  // Login/Registration
  loginClick: () => trackEvent('Login Click', 'UserAction', 'Login'),
  registrationClick: () => trackEvent('Registration Click', 'UserAction', 'Registration'),
  userSignedIn: () => trackEvent('UserSignedIn', 'UserAction', 'User Signed In'),
  userRegistered: () => trackEvent('UserRegistered', 'UserAction', 'User Registered'),
  alreadyLogin: (context = 'Login Checked') => trackEvent('Already Login', 'Already Login', context),

  // Search Events
  searchSubmitted: (searchType = 'web') => trackEvent('search_submitted', 'Search', searchType),
  searchTabClicked: (tabName) => trackEvent('search_tab_clicked', 'Search', tabName),
  
  // Navigation
  pageView: (pageName) => trackEvent('page_view', 'Navigation', pageName),
  navigationClick: (destination) => trackEvent('navigation_click', 'Navigation', destination),
  
  // Settings Events
  settingsUpdate: (updateType) => trackEvent('settings_update', 'Settings', updateType),
  externalLink: (linkType) => trackEvent('external_link_click', 'Settings', linkType),
  
  // Feature Usage
  featureUsed: (featureName) => trackEvent('feature_used', 'Features', featureName),
  buttonClicked: (buttonName) => trackEvent('button_clicked', 'UI', buttonName),
};

// Chat Events
export const trackChatEvent = {
  messageSent: (method = 'send_button') => trackEvent('chat_message_sent', 'Chat', method),
  newChatClicked: () => trackEvent('new_chat_clicked', 'Chat', 'new_chat_btn'),
  fileUploaded: (fileName) => trackEvent('chat_file_uploaded', 'Chat', fileName),
  suggestionCategoryClicked: (category) => trackEvent('suggestion_category_clicked', 'Chat', category),
  suggestionPromptClicked: (prompt) => trackEvent('suggestion_prompt_clicked', 'Chat', prompt),
};

// Widget Events
export const trackWidgetEvent = {
  tabClicked: (tabName) => trackEvent('widget_tab_clicked', 'Widget', tabName),
  searchSubmitted: (action) => trackEvent('widget_search_submitted', 'Widget', action),
};

// Mini-App Events
const EMPTY_MINIAPP_CONTEXT = {
  lesson_id: '',
  world_id: '',
  activity_type: '',
  step_id: '',
};

let miniAppAnalyticsContext = { ...EMPTY_MINIAPP_CONTEXT };

/**
 * LessonPage supplies the content context once for all activity engines. This
 * keeps engine-level analytics consistent without making every engine rebuild
 * the same GA parameters.
 */
export const setMiniAppAnalyticsContext = (context = {}) => {
  miniAppAnalyticsContext = {
    lesson_id: context.lesson_id || '',
    world_id: context.world_id || '',
    activity_type: context.activity_type || '',
    step_id: context.step_id || '',
  };
};

export const clearMiniAppAnalyticsContext = () => {
  miniAppAnalyticsContext = { ...EMPTY_MINIAPP_CONTEXT };
};

export const trackMiniAppEvent = {
  // Basic app events
  launched: (appName) => trackEvent('miniapp_launched', 'MiniApps', appName),
  interaction: (appName, action) =>
    trackEvent('miniapp_interaction', 'MiniApps', `${appName}_${action}`, {
      ...miniAppAnalyticsContext,
      interaction: action,
    }),
  completed: (appName) =>
    trackEvent('miniapp_completed', 'MiniApps', appName, {
      ...miniAppAnalyticsContext,
    }),
  
  // Specific app tracking
  coloring: {
    colorSelected: (color) => trackEvent('coloring_color_selected', 'MiniApps', 'coloring', { color }),
    canvasCleared: () => trackEvent('coloring_canvas_cleared', 'MiniApps', 'coloring'),
    drawingStarted: () => trackEvent('coloring_drawing_started', 'MiniApps', 'coloring'),
    drawingStopped: () => trackEvent('coloring_drawing_stopped', 'MiniApps', 'coloring'),
    brushSizeChanged: (size) => trackEvent('coloring_brush_size_changed', 'MiniApps', 'coloring', { brush_size: size }),
    toolChanged: (tool) => trackEvent('coloring_tool_changed', 'MiniApps', 'coloring', { tool }),
    downloadClicked: () => trackEvent('coloring_download_clicked', 'MiniApps', 'coloring'),
  },
  
  tictactoe: {
    gameStarted: (mode) => trackEvent('tictactoe_game_started', 'MiniApps', 'tictactoe', { mode }),
    moveMade: (position, player) => trackEvent('tictactoe_move_made', 'MiniApps', 'tictactoe', { position, player }),
    gameWon: (winner) => trackEvent('tictactoe_game_won', 'MiniApps', 'tictactoe', { winner }),
    gameDraw: () => trackEvent('tictactoe_game_draw', 'MiniApps', 'tictactoe'),
    newGameClicked: () => trackEvent('tictactoe_new_game_clicked', 'MiniApps', 'tictactoe'),
    modeChanged: (mode) => trackEvent('tictactoe_mode_changed', 'MiniApps', 'tictactoe', { mode }),
  },
  
  mathquiz: {
    quizStarted: (mode, difficulty, questionCount) => trackEvent('mathquiz_quiz_started', 'MiniApps', 'mathquiz', { 
      mode, 
      difficulty, 
      question_count: questionCount 
    }),
    questionAnswered: (isCorrect, difficulty, questionType) => trackEvent('mathquiz_question_answered', 'MiniApps', 'mathquiz', { 
      correct: isCorrect, 
      difficulty, 
      question_type: questionType 
    }),
    quizCompleted: (score, accuracy, totalQuestions, difficulty) => trackEvent('mathquiz_quiz_completed', 'MiniApps', 'mathquiz', { 
      score, 
      accuracy, 
      total_questions: totalQuestions, 
      difficulty 
    }),
    difficultyChanged: (difficulty) => trackEvent('mathquiz_difficulty_changed', 'MiniApps', 'mathquiz', { difficulty }),
    modeChanged: (mode) => trackEvent('mathquiz_mode_changed', 'MiniApps', 'mathquiz', { mode }),
    questionCountChanged: (count) => trackEvent('mathquiz_question_count_changed', 'MiniApps', 'mathquiz', { question_count: count }),
    streakUpdated: (streak) => trackEvent('mathquiz_streak_updated', 'MiniApps', 'mathquiz', { streak }),
    quizEnded: (reason) => trackEvent('mathquiz_quiz_ended', 'MiniApps', 'mathquiz', { reason }),
  },
  
  /** Guided hint overlay (activity engines) — snake_case event names per product spec */
  hintShown: (activityId, detail = {}) =>
    trackEvent('hint_shown', 'MiniApps', activityId, detail),
  hintRequested: (activityId, detail = {}) =>
    trackEvent('hint_requested', 'MiniApps', activityId, detail),
  hintAudioPlayed: (activityId, detail = {}) =>
    trackEvent('hint_audio_played', 'MiniApps', activityId, detail),
  hintExplanationShown: (activityId, detail = {}) =>
    trackEvent('hint_explanation_shown', 'MiniApps', activityId, detail),

  // Generic interaction tracking
  buttonClicked: (appName, buttonName) => trackEvent('miniapp_button_clicked', 'MiniApps', appName, { button: buttonName }),
  settingChanged: (appName, settingName, value) => trackEvent('miniapp_setting_changed', 'MiniApps', appName, { 
    setting: settingName, 
    value 
  }),
  errorOccurred: (appName, errorType) => trackEvent('miniapp_error', 'MiniApps', appName, { error_type: errorType }),
  timeSpent: (appName, duration) => trackEvent('miniapp_time_spent', 'MiniApps', appName, { duration_seconds: duration }),
};

// Search Results Events
export const trackSearchResultsEvent = {
  resultClicked: (resultType) => trackEvent('search_result_clicked', 'SearchResults', resultType),
  resultBlocked: (reason) => trackEvent('search_result_blocked', 'Safety', reason),
  filterApplied: (filterType) => trackEvent('search_filter_applied', 'SearchResults', filterType),
};

// Safety Events
export const trackSafetyEvent = {
  contentBlocked: (contentType) => trackEvent('content_blocked', 'Safety', contentType),
  safeSearchEnabled: () => trackEvent('safe_search_enabled', 'Safety', 'enabled'),
  parentalControlUsed: (controlType) => trackEvent('parental_control_used', 'Safety', controlType),
  keywordBlocked: (keyword, firebaseId = null) => trackEvent('keyword_blocked', 'Safety', keyword, {
    user_type: firebaseId ? 'logged_in' : 'guest',
  }),
};

// Error Events
export const trackErrorEvent = {
  apiError: (endpoint) => trackEvent('api_error', 'Errors', endpoint),
  searchError: (errorType) => trackEvent('search_error', 'Errors', errorType),
  chatError: (errorType) => trackEvent('chat_error', 'Errors', errorType),
};

// Performance Events
export const trackPerformanceEvent = {
  pageLoadTime: (loadTime) => trackEvent('page_load_time', 'Performance', 'load_time', { value: loadTime }),
  searchResponseTime: (responseTime) => trackEvent('search_response_time', 'Performance', 'response_time', { value: responseTime }),
  chatResponseTime: (responseTime) => trackEvent('chat_response_time', 'Performance', 'response_time', { value: responseTime }),
};

// Custom tracking for specific features
export const trackCustomEvent = (eventName, category, label, additionalParams = {}) => {
  trackEvent(eventName, category, label, additionalParams);
};

/**
 * Worlds, paths, lessons, and steps — GA4-friendly snake_case names + params for Explore funnels.
 */
export const trackLearning = {
  /** /topics/explorer — world picker */
  worldExplorerOpen: () => trackEvent('world_explorer_open', 'Learning', 'topics_explorer'),

  /** /topics — full lesson index */
  lessonCatalogOpen: () => trackEvent('lesson_catalog_open', 'Learning', 'lesson_index'),

  /** /topics/:worldId — paths for one world */
  worldOpen: (worldId, worldTitle) =>
    trackEvent('world_open', 'Learning', worldId, {
      world_id: worldId,
      world_title: worldTitle,
    }),

  /**
   * User started a path (guided lesson, mini-app, or search adventure).
   * @param {{ world_id: string, path_id: string, engage_type: 'guided_lesson'|'mini_app'|'search_adventure', lesson_id?: string, mini_app?: string }} p
   */
  pathEngage: (p) =>
    trackEvent('path_engage', 'Learning', p.path_id || 'unknown', {
      world_id: p.world_id,
      path_id: p.path_id,
      engage_type: p.engage_type,
      ...(p.lesson_id ? { lesson_id: p.lesson_id } : {}),
      ...(p.mini_app ? { mini_app: p.mini_app } : {}),
    }),

  /** Guided lesson page load (with progress) */
  lessonOpen: (worldId, lessonId, lessonTitle, totalSteps) =>
    trackEvent('lesson_open', 'Learning', lessonId, {
      world_id: worldId,
      lesson_id: lessonId,
      lesson_title: lessonTitle,
      total_steps: totalSteps,
    }),

  /** Each step shown (intro / activity / recap, etc.) */
  lessonStepView: (worldId, lessonId, lessonTitle, stepIndex, totalSteps, stepType, stepId) =>
    trackEvent('lesson_step_view', 'Learning', String(stepId || stepIndex), {
      world_id: worldId,
      lesson_id: lessonId,
      lesson_title: lessonTitle,
      step_index: stepIndex,
      total_steps: totalSteps,
      step_type: stepType || 'unknown',
    }),

  /** Finished recap / marked complete in-app */
  lessonComplete: (worldId, lessonId, lessonTitle, totalSteps) =>
    trackEvent('lesson_complete', 'Learning', lessonId, {
      world_id: worldId,
      lesson_id: lessonId,
      lesson_title: lessonTitle,
      step_index: Math.max(totalSteps - 1, 0),
      total_steps: totalSteps,
    }),

  /** Left the lesson before reaching the completion screen */
  lessonAbandoned: (worldId, lessonId, lessonTitle, stepIndex, totalSteps, detail = {}) =>
    trackEvent('lesson_abandoned', 'Learning', lessonId, {
      world_id: worldId,
      lesson_id: lessonId,
      lesson_title: lessonTitle,
      step_index: stepIndex,
      total_steps: totalSteps,
      ...detail,
    }),
};

/**
 * Story bridge events — fired when a Kids Place story hands off to an Explore lesson.
 * trackStoryBridgeComplete fires in Kids Place Android, not here; see docs/KIDS_PLACE_BRIDGE_SPEC.md.
 */
export const trackStoryBridgeShown = ({ storyId, character, lessonId }) =>
  trackEvent('story_bridge_shown', 'StoryBridge', lessonId || 'unknown', {
    story_id: storyId || null,
    character: character || null,
    lesson_id: lessonId || null,
  });

export const trackStoryBridgeTapped = ({ storyId, character, lessonId }) =>
  trackEvent('story_bridge_tapped', 'StoryBridge', lessonId || 'unknown', {
    story_id: storyId || null,
    character: character || null,
    lesson_id: lessonId || null,
  });

export const trackStoryBridgeSkipped = ({ storyId, character, lessonId }) =>
  trackEvent('story_bridge_skipped', 'StoryBridge', lessonId || 'unknown', {
    story_id: storyId || null,
    character: character || null,
    lesson_id: lessonId || null,
  });

export const trackStoryFeedback = ({ storyId, character, rating, totalScenes, completedFully }) =>
  trackEvent('story_feedback', 'Feedback', storyId || 'unknown', {
    story_id: storyId || null,
    character: character || null,
    rating,
    total_scenes: totalScenes,
    completed_fully: completedFully,
  });

export const trackLessonFeedback = ({ lessonId, worldId, rating, totalSteps, completedFully }) =>
  trackEvent('lesson_feedback', 'Feedback', lessonId || 'unknown', {
    lesson_id: lessonId || null,
    world_id: worldId || null,
    rating,
    total_steps: totalSteps,
    completed_fully: completedFully,
  });

// Story lifecycle events
export const trackStoryOpen = ({ storyId, character, storyTitle, totalScenes }) =>
  trackEvent('story_open', 'Stories', storyId || 'unknown', {
    story_id: storyId || null,
    character: character || null,
    story_title: storyTitle || null,
    total_scenes: totalScenes ?? null,
  });

export const trackStorySceneView = ({ storyId, character, storyTitle, sceneIndex, totalScenes }) =>
  trackEvent('story_scene_view', 'Stories', storyId || 'unknown', {
    story_id: storyId || null,
    character: character || null,
    story_title: storyTitle || null,
    scene_index: sceneIndex,
    total_scenes: totalScenes ?? null,
    pct_complete: totalScenes > 0 ? Math.round((sceneIndex / totalScenes) * 100) : 0,
  });

export const trackStoryComplete = ({ storyId, character, storyTitle, totalScenes }) =>
  trackEvent('story_complete', 'Stories', storyId || 'unknown', {
    story_id: storyId || null,
    character: character || null,
    story_title: storyTitle || null,
    scene_index: Math.max((totalScenes ?? 0) - 1, 0),
    total_scenes: totalScenes ?? null,
  });

export const trackStoryAbandoned = ({
  storyId,
  character,
  storyTitle,
  lastSceneIndex,
  totalScenes,
}) =>
  trackEvent('story_abandoned', 'Stories', storyId || 'unknown', {
    story_id: storyId || null,
    character: character || null,
    story_title: storyTitle || null,
    scene_index: lastSceneIndex,
    last_scene_index: lastSceneIndex,
    total_scenes: totalScenes ?? null,
    pct_reached: totalScenes > 0 ? Math.round((lastSceneIndex / totalScenes) * 100) : 0,
  });

export const trackCharacterSelected = ({ character }) =>
  trackEvent('character_selected', 'Stories', character || 'unknown', {
    character: character || null,
  });

export const trackStorySelected = ({ storyId, character, storyIndex }) =>
  trackEvent('story_selected', 'Stories', storyId || 'unknown', {
    story_id: storyId || null,
    character: character || null,
    story_index: storyIndex ?? null,
  });

// Track page views for React Router
export const trackPageView = (pathname, pageTitle) => {
  const title = pageTitle || document.title;
  if (!isAnalyticsActive()) {
    if (import.meta.env?.DEV) {
      console.debug('[GA]', 'page_view', { page_path: pathname, page_title: title }, '(local preview; not sent)');
    }
    return;
  }
  if (isGtagAvailable()) {
    window.gtag('config', GA_MEASUREMENT_ID, {
      ...GA_GTAG_PRIVACY_OPTIONS,
      page_path: pathname,
      page_title: title,
    });
    console.debug('[GA] Page view:', pathname, title, GA_MEASUREMENT_ID);
  }
};

// Track timing for performance monitoring
export const trackTiming = (category, variable, time) => {
  if (!isAnalyticsActive()) {
    return;
  }
  if (isGtagAvailable()) {
    window.gtag('event', 'timing_complete', {
      name: variable,
      value: time,
      event_category: category
    });
    console.debug('[GA] Timing:', category, variable, time);
  }
};

// Track exceptions
export const trackException = (description, fatal = false) => {
  if (!isAnalyticsActive()) {
    return;
  }
  if (isGtagAvailable()) {
    window.gtag('event', 'exception', {
      description: description,
      fatal: fatal
    });
    console.debug('[GA] Exception:', description, fatal);
  }
};

// Initialize analytics (can be called on app startup)
export const initializeAnalytics = () => {
  if (typeof window === 'undefined' || !shouldUseGoogleAnalytics(window.location.hostname)) {
    console.debug('[GA] Disabled (not production kiddoware host)');
    return;
  }
  let attempts = 0;
  const maxAttempts = 50;
  const checkGtag = () => {
    if (isGtagAvailable()) {
      console.debug('[GA] Analytics initialized');
      trackPageView(window.location.pathname);
    } else if (attempts < maxAttempts) {
      attempts += 1;
      setTimeout(checkGtag, 100);
    } else {
      console.debug('[GA] gtag not loaded after retries, skipping init');
    }
  };

  checkGtag();
};

// Track time spent in mini-apps
export const trackMiniAppTimeSpent = (appName, startTime) => {
  const endTime = Date.now();
  const duration = Math.round((endTime - startTime) / 1000); // Convert to seconds
  
  if (duration > 0) {
    trackMiniAppEvent.timeSpent(appName, duration);
  }
};

export default {
  identifyUser,
  trackEvent,
  trackUserAction,
  trackChatEvent,
  trackWidgetEvent,
  trackMiniAppEvent,
  trackSearchResultsEvent,
  trackSafetyEvent,
  trackErrorEvent,
  trackPerformanceEvent,
  trackCustomEvent,
  trackLearning,
  trackPageView,
  trackTiming,
  trackException,
  initializeAnalytics,
}; 